/**
 * Copyright (c) 2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { __awaiter, __generator } from "tslib";
import { Mesh } from '../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../mol-geo/geometry/mesh/mesh-builder';
import { BoxCage } from '../../mol-geo/primitive/box';
import { Box3D, Sphere3D } from '../../mol-math/geometry';
import { Mat4, Vec3 } from '../../mol-math/linear-algebra';
import { Shape } from '../../mol-model/shape';
import { Task } from '../../mol-task';
import { ColorNames } from '../../mol-util/color/names';
import { ParamDefinition as PD } from '../../mol-util/param-definition';
import { PluginStateObject as SO, PluginStateTransform } from '../objects';
export { BoxShape3D };
var BoxShape3D = PluginStateTransform.BuiltIn({
    name: 'box-shape-3d',
    display: 'Box Shape',
    from: SO.Root,
    to: SO.Shape.Provider,
    params: {
        bottomLeft: PD.Vec3(Vec3()),
        topRight: PD.Vec3(Vec3.create(1, 1, 1)),
        radius: PD.Numeric(0.15, { min: 0.01, max: 4, step: 0.01 }),
        color: PD.Color(ColorNames.red)
    }
})({
    canAutoUpdate: function () {
        return true;
    },
    apply: function (_a) {
        var _this = this;
        var params = _a.params;
        return Task.create('Shape Representation', function (ctx) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, new SO.Shape.Provider({
                        label: 'Box',
                        data: params,
                        params: Mesh.Params,
                        getShape: function (_, data) {
                            var mesh = getBoxMesh(Box3D.create(params.bottomLeft, params.topRight), params.radius);
                            return Shape.create('Box', data, mesh, function () { return data.color; }, function () { return 1; }, function () { return 'Box'; });
                        },
                        geometryUtils: Mesh.Utils
                    }, { label: 'Box' })];
            });
        }); });
    }
});
export function getBoxMesh(box, radius, oldMesh) {
    var diag = Vec3.sub(Vec3(), box.max, box.min);
    var translateUnit = Mat4.fromTranslation(Mat4(), Vec3.create(0.5, 0.5, 0.5));
    var scale = Mat4.fromScaling(Mat4(), diag);
    var translate = Mat4.fromTranslation(Mat4(), box.min);
    var transform = Mat4.mul3(Mat4(), translate, scale, translateUnit);
    // TODO: optimize?
    var state = MeshBuilder.createState(256, 128, oldMesh);
    state.currentGroup = 1;
    MeshBuilder.addCage(state, transform, BoxCage(), radius, 2, 20);
    var mesh = MeshBuilder.getMesh(state);
    var center = Vec3.scaleAndAdd(Vec3(), box.min, diag, 0.5);
    var sphereRadius = Vec3.distance(box.min, center);
    mesh.setBoundingSphere(Sphere3D.create(center, sphereRadius));
    return mesh;
}
